import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Shield, AlertTriangle, Info, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export const SecurityDashboard = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [lastScan, setLastScan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadLastScan();
  }, []);

  const loadLastScan = async () => {
    try {
      const { data } = await supabase
        .from('security_scans')
        .select('*')
        .order('scanned_at', { ascending: false })
        .limit(1)
        .single();

      setLastScan(data);
    } catch (error) {
      console.error('Error loading scan:', error);
    } finally {
      setLoading(false);
    }
  };

  const runSecurityScan = async () => {
    setIsScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke('security-scan');

      if (error) throw error;

      toast({
        title: "Security Scan Complete",
        description: `Found ${data.scan.counts.total} finding(s)`,
      });

      loadLastScan();
    } catch (error: any) {
      toast({
        title: "Scan Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsScanning(false);
    }
  };

  const getSeverityIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'warn':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-600" />;
      default:
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    }
  };

  const getSeverityBadge = (level: string) => {
    const variants: any = {
      error: "destructive",
      warn: "default",
      info: "secondary"
    };
    return <Badge variant={variants[level] || "default"}>{level.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Dashboard
              </CardTitle>
              <CardDescription>
                Automated security monitoring and vulnerability detection
              </CardDescription>
            </div>
            <Button
              onClick={runSecurityScan}
              disabled={isScanning}
            >
              {isScanning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Run Scan
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lastScan ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Last scan: {new Date(lastScan.scanned_at).toLocaleString()}</span>
                <span>Duration: {lastScan.scan_duration_ms}ms</span>
              </div>

              {lastScan.severity_counts && (
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold text-red-600">
                        {lastScan.severity_counts.error || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Critical Issues</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold text-yellow-600">
                        {lastScan.severity_counts.warn || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Warnings</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-2xl font-bold text-blue-600">
                        {lastScan.severity_counts.info || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Info</div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No scans performed yet. Run your first security scan to detect vulnerabilities.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {lastScan?.findings && lastScan.findings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Security Findings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {lastScan.findings.map((finding: any, index: number) => (
              <Alert key={index}>
                <div className="flex items-start gap-3">
                  {getSeverityIcon(finding.level)}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <AlertTitle>{finding.name}</AlertTitle>
                      {getSeverityBadge(finding.level)}
                    </div>
                    <AlertDescription className="space-y-1">
                      <p>{finding.description}</p>
                      {finding.details && (
                        <p className="text-sm text-muted-foreground">{finding.details}</p>
                      )}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
